import { Injectable } from '@angular/core'
import { SharedModule } from '@tv3/shared/shared.module'
import {
  Channel,
  ChannelNameTokens,
  ChannelService,
  ConfirmDialogService,
  ConnectedChannelAccountResponse,
  DialogService,
  DisconnectChannel,
  DisconnectChannelComplete,
  removeRateMappingForChannel,
  Toaster,
} from '@tokeet-frontend/tv3-platform'
import { switchMap, take, tap, map } from 'rxjs/operators'
import { select, Store } from '@ngrx/store'
import * as R from 'ramda'
import { LoadConnections, RemoveLocalConnectionsByIds } from '@tv3/store/connection/connection.actions'
import { Actions, ofType } from '@ngrx/effects'
import { selectConnectionsByChannelId } from '@tv3/store/connection/connection.selectors'
import { Connection } from '@tv3/store/connection/connection.model'
import { CtripChannelService, TiketChannelService } from '@tokeet-frontend/channels'
import { ChannelsOverlayComponent } from '../shared/channels-overlay/channels-overlay.component'
import * as lodash from 'lodash'

@Injectable({
  providedIn: SharedModule,
})
export class ChannelHelperService {
  constructor(
    private store: Store<any>,
    private actions$: Actions,
    private toaster: Toaster,
    private dialogService: DialogService,
    private confirmDialog: ConfirmDialogService,
    private channelService: ChannelService,
    private tiketChannel: TiketChannelService,
    private ctripChannel: CtripChannelService
  ) {}

  connectChannel(channelType?: 'api' | 'ical' | 'custom') {
    return this.dialogService.openDrawer(ChannelsOverlayComponent, {
      panelClass: 'drawer-full-70',
      data: { channelType },
    })
  }

  getChannelAccounts(channel: Channel) {
    if (channel.name === ChannelNameTokens.Tiket) {
      return this.tiketChannel
        .getStatus()
        .pipe(map(({ hotelIds, connectedAt }) => lodash.map(hotelIds, (id) => ({ propertyId: id, connectedAt }))))
    } else if (channel.name === ChannelNameTokens.Trip) {
      return this.ctripChannel
        .getStatus()
        .pipe(map(({ hotelIds, connectedAt }) => lodash.map(hotelIds, (id) => ({ propertyId: id, connectedAt }))))
    } else {
      return this.channelService.getConnectedAccounts(channel.id)
    }
  }

  // TV4-5830 (QA/testing helper, non-production only): force the account's Booking.com
  // connection(s) into the disabled / needs-reconnect state, then reload connections so the
  // reconnect banner appears. The button that calls this is hidden on production, and the backend
  // endpoint refuses to run there as a second line of defence.
  simulateBookingError(channel: Channel) {
    return this.channelService.forceDisableBooking().pipe(
      tap(() => {
        this.store.dispatch(LoadConnections({ force: true }))
        this.toaster.success(
          $localize`${channel.friendlyName} connection(s) disabled. The reconnect banner should now appear.`
        )
      })
    )
  }

  disconnectAccount(channel: Channel, channelAccount) {
    const channelId = channelAccount.channelId || channel.id
    const channelName = channelAccount.name || channel.name
    const propertyId = channelAccount.propertyId

    return this.confirmDialog
      .confirm({
        title: $localize`Disconnect this Account?`,
        body: $localize`This will unlink all rentals connected to this channel and disconnect this account from your AdvanceCM account. <br/>Are you sure you want to Disconnect this account?`,
      })
      .pipe(
        switchMap(() => {
          if (channel.name === ChannelNameTokens.Tiket) {
            return this.tiketChannel
              .disconnect(propertyId)
              .pipe(tap(() => this.toaster.success($localize`Account disconnected successfully.`)))
          } else if (channel.name === ChannelNameTokens.Trip) {
            return this.ctripChannel
              .disconnect(propertyId as string)
              .pipe(tap(() => this.toaster.success($localize`Account disconnected successfully.`)))
          } else {
            channelAccount = channelAccount as ConnectedChannelAccountResponse
            this.store.dispatch(
              DisconnectChannel({
                channelName,
                channelId,
              })
            )
            return this.actions$.pipe(ofType(DisconnectChannelComplete.type))
          }
        }),
        switchMap(() =>
          this.store.pipe(
            select(selectConnectionsByChannelId, { id: channelId }),
            tap((connections: Connection[]) => {
              // remove local connection data
              this.store.dispatch(
                RemoveLocalConnectionsByIds({
                  connectionIds: R.map((c) => c.id, connections),
                })
              )
              this.store.dispatch(removeRateMappingForChannel({ channelId: channelId }))
            })
          )
        ),
        take(1)
      )
  }
}
